import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from 'lib/request/api';
import { subscribe } from 'lib/pusher';
import { useEval3Page } from './context';
import {
  electionGroupsOf,
  electionResultKey,
  electionTermsKey,
  isElectionItemComplete,
  type ElectionResult,
  type PreviewElection,
} from './interest-modeling-election';
import type { InterestPreviewPending } from './interest-modeling-preview';
import type { PreviewStatus } from './use-carry-preview';
import type { CalculateRequest, InterestItem } from './types';

// A reprice runs the calculator; use the eval3 long timeout so a slow reprice degrades
// to a soft error (last-good retained) rather than ky's 30s default cutting it short.
const PREVIEW_TIMEOUT_MS = 300_000;

// A DRAFT eval (`eval/new`) has no EvaluationCalculation row, so no member route —
// its previews go to the draft endpoint, carrying the draft's working config
// (serialized by the same buildCalculateRequest Calculate uses) in the body.
const previewPath = (calculationId: number | null) => {
  return calculationId === null
    ? 'evaluations/draft_interest_preview'
    : `evaluations/calculations/${calculationId}/interest_preview`;
};

/** Election reprice success — the tract payload's `elections` (all three variants). */
interface ElectionPreviewSuccess {
  token: string;
  tract: { elections?: PreviewElection[] };
}

type ElectionPreviewResponse = ElectionPreviewSuccess | InterestPreviewPending;

export interface ElectionPreview {
  /** tractId → the three variant results (carry / participate / lease); absent ⇒ not repriced yet. */
  results: ReadonlyMap<string, ElectionResult[]>;
  status: PreviewStatus;
  /** Reprice one election item (the on-blur trigger); gated on completeness + de-duped. */
  reprice: (item: InterestItem) => void;
  /** Drop a removed tract from the session (evicts its cached results). */
  drop: (tractId: string) => void;
}

export interface UseElectionPreviewArgs {
  /** The working config's interest items (election ones are warmed automatically). */
  interestItems: InterestItem[];
  /**
   * DRAFT (row-less) evals only: builds the draft's Calculate-shaped request base —
   * the SAME serializer Calculate uses (`buildCalculateRequest`, at the callsite so
   * the form coupling stays there) — which the draft endpoint normalizes into the
   * in-memory calculation it previews against. Present ⇒ the null-calculationId
   * skip is lifted and requests go to the draft endpoint; absent on a draft ⇒ the
   * hook stays idle (previews need the draft context). Ignored for a real eval.
   */
  buildDraftRequest?: () => CalculateRequest;
  /**
   * DRAFT evals only: a stable signature of the PRICING-RELEVANT draft config —
   * when it changes, every cached result and the session's prepared frame were
   * priced under the OLD config, so the hook resets the preview session and
   * re-warms. Also the recovery path for a warm that 422'd on a not-yet-valid
   * draft (e.g. no scenario picked yet). Ignored for a real eval. Mirrors the
   * carry hook.
   */
  draftSessionKey?: string;
}

/**
 * Owns the ephemeral interest-preview lifecycle for the UMI election surface. A focused
 * sibling of `useCarryPreview`: same Redis-free token echo / seq
 * supersede / soft-error / Case-1 `preparing`+Pusher patterns, but UMI is PER-TRACT —
 * no cross-tract recombination, no portfolio, no aggregate/byAsset/byProperty view. Each
 * `reprice(item)` POSTs the election item to the shared `interest_preview` endpoint and
 * caches `response.tract.elections` (all three variants — the calculator computes them
 * regardless of `electedElection`) keyed by tract AND property group
 * ({@link electionResultKey}).
 *
 * State machine, per tract:
 *   • warm — each COMPLETE election tract is repriced once so its outcome cards are ready
 *     without visiting the tract (drained one per pass, once per terms — no retry loop).
 *   • reprice — the active tract's terms change + a field blurs → reprice (completeness-
 *     gated; de-duped by a terms key; a newer edit SUPERSEDES a stale response via seq).
 *   • drop — remove → evict the tract's cached results + supersede any in-flight reprice.
 *     No server drop: there is no carry recombine to trigger, and the frame blob is torn
 *     down by the deferred TTL sweep.
 *   • Case-1 — a fresh package returns needs_prepare/preparing → show "preparing…",
 *     subscribe to Pusher `interest_preview_<token>`; on `preview_prepared` re-drain the
 *     warm loop, on failure surface a quiet error (editing stays live; next edit retries).
 *   • never hard-fail — a soft error keeps the last-good results + surfaces `error`.
 *
 * @param {UseElectionPreviewArgs} args - The interest items.
 * @return {ElectionPreview} The per-tract results + reprice/drop actions.
 */
export function useElectionPreview({
  interestItems,
  buildDraftRequest,
  draftSessionKey,
}: UseElectionPreviewArgs): ElectionPreview {
  const { calculationId } = useEval3Page();
  // A preview target exists when the eval has a row (member route) or the caller
  // supplied the draft context (draft endpoint). Without either, stay idle.
  const previewEnabled =
    calculationId !== null || buildDraftRequest !== undefined;
  // Ref so reprice reads the latest builder at dispatch time without taking it as
  // a dependency (same pattern as the carry hook).
  const buildDraftRequestRef = useRef(buildDraftRequest);
  buildDraftRequestRef.current = buildDraftRequest;

  const [results, setResults] = useState<Map<string, ElectionResult[]>>(
    () => new Map()
  );
  const [status, setStatus] = useState<PreviewStatus>('idle');
  // Bumped on `preview_prepared` to re-run the warm drain once the Case-1 frame exists.
  const [prepareEpoch, setPrepareEpoch] = useState(0);

  // Control plane (refs — not render state).
  const tokenRef = useRef<string | null>(null);
  const seqRef = useRef<Map<string, number>>(new Map());
  const inFlightRef = useRef<Map<string, string>>(new Map());
  const lastKeyRef = useRef<Map<string, string>>(new Map());
  // The terms a tract's automatic warm was last attempted for — so a soft-errored /
  // preparing warm doesn't re-fire in a loop (an explicit reprice/edit still retries).
  const warmAttemptRef = useRef<Map<string, string>>(new Map());

  // Draft-config invalidation: a pricing-relevant change to the LIVE draft config
  // obsoletes the whole session — the prepared frame and every cached result carry
  // the old config's prices. Reset to a fresh session: drop the token (a new
  // prepare is dispatched under a new one), supersede in-flight responses, clear
  // the cache + de-dupe/warm latches, and let the warm drain start over. Skipped
  // on mount and on real evals (the key is undefined and never changes there).
  // Mirrors the carry hook.
  const prevDraftKeyRef = useRef(draftSessionKey);
  useEffect(() => {
    if (prevDraftKeyRef.current === draftSessionKey) {
      return;
    }
    prevDraftKeyRef.current = draftSessionKey;
    tokenRef.current = null;
    seqRef.current.forEach((seq, tract) => seqRef.current.set(tract, seq + 1));
    inFlightRef.current.clear();
    lastKeyRef.current.clear();
    warmAttemptRef.current.clear();
    setResults(new Map());
    setStatus('idle');
    setPrepareEpoch((epoch) => epoch + 1);
  }, [draftSessionKey]);

  const reprice = useCallback(
    (item: InterestItem) => {
      if (!previewEnabled || !isElectionItemComplete(item)) {
        return;
      }
      const tractId = item.tractId;
      const terms = electionTermsKey(item);
      if (
        inFlightRef.current.get(tractId) === terms ||
        lastKeyRef.current.get(tractId) === terms
      ) {
        return; // already in flight / cached for these exact terms
      }

      inFlightRef.current.set(tractId, terms);
      const mySeq = (seqRef.current.get(tractId) ?? 0) + 1;
      seqRef.current.set(tractId, mySeq);

      void (async () => {
        try {
          const response = await api
            .post(previewPath(calculationId), {
              json: {
                // A draft carries its Calculate-shaped working config so the BE can
                // assemble the in-memory calculation it previews against.
                ...(calculationId === null
                  ? buildDraftRequestRef.current?.()
                  : undefined),
                tractId,
                item,
                token: tokenRef.current ?? undefined,
              },
              timeout: PREVIEW_TIMEOUT_MS,
            })
            .json<ElectionPreviewResponse>();

          if (seqRef.current.get(tractId) !== mySeq) {
            return; // superseded by a newer edit
          }
          tokenRef.current = response.token ?? tokenRef.current;
          inFlightRef.current.delete(tractId);

          if ('status' in response) {
            if (response.status === 'error') {
              setStatus('error'); // soft — keep last-good
            } else if (response.status === 'needs_recalculation') {
              // Old committed calc (predates the pairs sidecar): no prepare will
              // ever complete, so never mark 'preparing' (that would subscribe to a
              // Pusher channel that never fires — an eternal spinner). Mirror the
              // carry hook: prompt a recalc instead.
              setStatus('needs_recalculation');
            } else {
              setStatus('preparing'); // Case-1 needs_prepare / preparing
            }
            return;
          }

          const elections = response.tract?.elections ?? [];
          // One election item per request ⇒ one tract, but one entry per property
          // group. Entries for other tracts are ignored only when the response
          // actually echoes ids — an un-echoed payload is taken as this tract's.
          const mine = elections.filter(
            (entry) =>
              entry.tractId === undefined ||
              entry.tractId === null ||
              String(entry.tractId) === String(tractId)
          );
          setResults((prev) => {
            const next = new Map(prev);
            // Evict this tract's previous groups first, so a group the user removed
            // cannot leave stale outcomes behind for a later group to inherit.
            [...next.keys()]
              .filter((key) => key.startsWith(`${tractId}:`))
              .forEach((key) => next.delete(key));
            mine.forEach((entry) => {
              next.set(
                electionResultKey(tractId, entry.groupId),
                entry.results ?? []
              );
            });
            return next;
          });
          setStatus('ready');
          lastKeyRef.current.set(tractId, terms);
        } catch {
          if (seqRef.current.get(tractId) !== mySeq) {
            return;
          }
          inFlightRef.current.delete(tractId);
          setStatus('error'); // soft — never block editing
        }
      })();
    },
    [calculationId, previewEnabled]
  );

  const drop = useCallback((tractId: string) => {
    // Supersede any in-flight reprice (its late response must not re-add the tract) and
    // evict local state. No server drop — UMI has no recombine; the TTL sweep tears down
    // the frame blob.
    seqRef.current.set(tractId, (seqRef.current.get(tractId) ?? 0) + 1);
    inFlightRef.current.delete(tractId);
    lastKeyRef.current.delete(tractId);
    warmAttemptRef.current.delete(tractId);
    setResults((prev) => {
      const next = new Map(prev);
      // Keyed per group, so every one of the tract's keys goes.
      [...next.keys()]
        .filter((key) => key.startsWith(`${tractId}:`))
        .forEach((key) => next.delete(key));
      return next;
    });
  }, []);

  // Warm drain: reprice the first complete, uncached, not-in-flight election tract, once
  // per terms. One per pass (the cache change re-runs the effect for the next).
  useEffect(() => {
    if (
      !previewEnabled ||
      status === 'preparing' ||
      status === 'needs_recalculation'
    ) {
      return;
    }
    for (const item of interestItems) {
      if (item.kind !== 'election') {
        continue;
      }
      const tractId = item.tractId;
      // Cached ⇒ EVERY priced group has results. Not "some key for this tract exists":
      // a tract keeps its first group's results when a second is added, so an
      // any-key probe reads the tract as warm and the new group never prices —
      // it sits on "Modeling elections…" forever (found in sandbox UAT). An empty
      // group prices nothing and is excluded, as it is from the completeness gate.
      const priced = electionGroupsOf(item).filter(
        (group) => group.wellIds.length > 0
      );
      const cached =
        priced.length > 0 &&
        priced.every((group) =>
          results.has(electionResultKey(tractId, group.id))
        );
      if (cached || inFlightRef.current.has(tractId)) {
        continue;
      }
      if (!isElectionItemComplete(item)) {
        continue;
      }
      const terms = electionTermsKey(item);
      if (warmAttemptRef.current.get(tractId) === terms) {
        continue; // already warmed these terms — no auto-retry loop
      }
      warmAttemptRef.current.set(tractId, terms);
      reprice(item);
      break;
    }
  }, [interestItems, results, status, prepareEpoch, previewEnabled, reprice]);

  // Case-1 async prepare: while preparing, subscribe to the preview channel; on
  // `preview_prepared` re-drain the warm loop (frame now exists), on failure surface a
  // quiet error. Keyed on `status` — the token is stable for the whole preparing spell.
  useEffect(() => {
    if (status !== 'preparing') {
      return undefined;
    }
    const token = tokenRef.current;
    if (!token) {
      return undefined;
    }
    const channel = subscribe(`interest_preview_${token}`);
    const onPrepared = () => {
      warmAttemptRef.current.clear();
      setStatus('idle');
      setPrepareEpoch((epoch) => epoch + 1);
    };
    const onFailed = () => setStatus('error');
    channel.bind('preview_prepared', onPrepared);
    channel.bind('preview_prepare_failed', onFailed);
    return () => {
      channel.unbind('preview_prepared', onPrepared);
      channel.unbind('preview_prepare_failed', onFailed);
      channel.unsubscribe();
    };
  }, [status]);

  return { results, status, reprice, drop };
}
